import React, { FC, useEffect, useState } from 'react';
import { useTranslate } from '@im/react-localization';
import { HorizontalProgressBar } from '@im/ui-lib/HorizontalProgressBar';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import { formatFileSize } from 'src/util';

export interface UploadFileDetailsProps {
  file: File;
  onResolved: (result?: any) => void;
  uploadFileApi: any;
}

const UploadFileDetails: FC<UploadFileDetailsProps> = ({
  file,
  onResolved,
  uploadFileApi,
}) => {
  const translate = useTranslate();
  const [progress, setProgress] = useState<number>(0);
  const { error, isError, isLoading, mutateAsync } = uploadFileApi(setProgress);
  useEffect(() => {
    mutateAsync({
      file,
    })
      .then(onResolved)
      .catch(onResolved);
  }, [file]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    isError && onResolved(error);
  }, [isError]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <Stack direction="row" justifyContent="center" spacing={0}>
      <Button
        sx={(theme) => ({
          alignContent: 'flex-start',
          backgroundColor: 'transparent !important',
          boxShadow: `${theme.shadows[0]} !important`,
          flexWrap: 'wrap',
          padding: '0px !important',
          pointerEvents: 'none',
        })}
      >
        {!isError ? (
          <DescriptionOutlinedIcon
            sx={(theme) => ({ color: theme.palette.grey[600] })}
          />
        ) : (
          <WarningAmberIcon
            sx={(theme) => ({ color: theme.palette.error[700] })}
          />
        )}
      </Button>
      <Stack direction="column" sx={{ width: '100%' }}>
        <Stack direction="row">
          <Box sx={{ minWidth: '300px' }}>
            <Typography
              sx={(theme) => ({
                fontSize: theme.typography.body2.fontSize,
                lineHeight: theme.typography.body2.lineHeight,
              })}
            >
              {file.name}
            </Typography>
          </Box>
        </Stack>
        <Typography
          sx={(theme) => ({
            color: !isError
              ? 'inherit'
              : `${theme.palette.error[700]} !important`,
            fontSize: theme.typography.pxToRem(10),
            fontWeight: theme.typography.fontWeightMedium,
            lineHeight: theme.typography.body2.fontSize,
            marginTop: '0px',
          })}
        >
          {!isError ? formatFileSize(file.size) : file.name}
        </Typography>
        {!isError && <HorizontalProgressBar value={progress} />}
        {!isError && isLoading && (
          <Stack
            direction="row"
            gap={2}
            justifyContent="center"
            sx={(theme) => ({ marginTop: theme.spacing(1.25) })}
          >
            <Typography
              sx={(theme) => ({
                fontSize: theme.typography.body2.fontSize,
                lineHeight: theme.typography.body2.lineHeight,
              })}
            >
              {translate('CustomerGroups.uploadFileProcessing')}
            </Typography>
            <CircularProgress size={16} />
          </Stack>
        )}
      </Stack>
    </Stack>
  );
};

export default UploadFileDetails;
